import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { KeyRound } from 'lucide-react';
import api from '../../api/client';
import { normalizeUnit, kmToUnit, speedUnitLabel } from '../../utils/units';
import OdometerDisplay from './OdometerDisplay';
import TodayTrail from './TodayTrail';
import './FleetMap.css';

const FLEET_COLORS = ['#e63946', '#4cc9f0', '#ffb703', '#8ac926', '#b298dc', '#f4a261'];

function colorForIndex(i: number): string {
  return FLEET_COLORS[i % FLEET_COLORS.length];
}

function makeFleetIcon(color: string, label: string): L.DivIcon {
  return L.divIcon({
    html: `
      <div class="fleet-marker">
        <div class="fleet-marker-label">${label}</div>
        <div class="fleet-marker-pin" style="background:${color}"></div>
      </div>
    `,
    iconSize: [90, 54],
    iconAnchor: [45, 54],
    className: '',
  });
}

function powerColor(v: number): string {
  if (v > 13.0) return '#4caf50';
  if (v >= 12.0) return '#ffc107';
  return '#e63946';
}

function satColor(s: number): string {
  if (s >= 4) return '#4caf50';
  if (s >= 2) return '#ffc107';
  return '#e63946';
}

interface Position {
  device_id: string;
  latitude: number;
  longitude: number;
  speed?: number;
  timestamp: string;
  attributes?: { power?: number; battery?: number; ignition?: boolean; sat?: number; [key: string]: unknown };
}

interface DeviceLite {
  id: string;
  name: string;
  plate_number?: string | null;
  preferred_unit?: string;
}

interface Geofence {
  id: string;
  center_lat: number;
  center_lon: number;
  radius_m: number;
}

interface Props {
  devices: DeviceLite[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function FitAllBounds({ positions }: { positions: Position[] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length === 0) return;
    if (positions.length === 1) {
      map.setView([positions[0].latitude, positions[0].longitude], 13);
      return;
    }
    const bounds = L.latLngBounds(positions.map((p) => [p.latitude, p.longitude] as [number, number]));
    map.fitBounds(bounds, { padding: [50, 50] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions.length]);
  return null;
}

function FlyToSelected({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => { map.flyTo([lat, lng], 15, { duration: 1.0 }); }, [lat, lng, map]);
  return null;
}

export default function FleetMap({ devices, selectedId, onSelect }: Props) {
  const [positions, setPositions] = useState<Record<string, Position>>({});
  const [geofence, setGeofence] = useState<Geofence | null>(null);
  const [odometerKm, setOdometerKm] = useState<number | null>(null);
  const unit = normalizeUnit(devices.find((d) => d.id === selectedId)?.preferred_unit);

  const fetchPositions = async () => {
    try {
      const { data } = await api.get('/devices/positions');
      const map: Record<string, Position> = {};
      (data as Position[]).forEach((p) => { map[p.device_id] = p; });
      setPositions(map);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchPositions();
    const interval = setInterval(fetchPositions, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) { setGeofence(null); return; }
    api.get(`/geofences/${selectedId}`)
      .then((r) => setGeofence(r.data.length > 0 ? r.data[0] : null))
      .catch(() => setGeofence(null));
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) { setOdometerKm(null); return; }
    const fetchOdometer = () => {
      api.get(`/devices/${selectedId}/odometer`)
        .then((r) => setOdometerKm(r.data.total_distance_km))
        .catch(() => setOdometerKm(null));
    };
    fetchOdometer();
    const interval = setInterval(fetchOdometer, 30000);
    return () => clearInterval(interval);
  }, [selectedId]);

  const deviceIndex = useMemo(() => {
    const m: Record<string, number> = {};
    devices.forEach((d, i) => { m[d.id] = i; });
    return m;
  }, [devices]);

  const positionList = Object.values(positions).filter((p) => p.latitude !== 0);
  const selectedPosition = selectedId ? positions[selectedId] : null;
  const selectedDevice = selectedId ? devices.find((d) => d.id === selectedId) : null;
  const selectedColor = selectedId != null ? colorForIndex(deviceIndex[selectedId] ?? 0) : '#e63946';

  const defaultCenter: [number, number] = positionList.length > 0
    ? [positionList[0].latitude, positionList[0].longitude]
    : [52.2297, 21.0122];

  return (
    <div className="live-map-wrapper">
      <div className="map-container">
        <MapContainer center={defaultCenter} zoom={6} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {!selectedId && <FitAllBounds positions={positionList} />}
          {selectedId && selectedPosition && (
            <FlyToSelected lat={selectedPosition.latitude} lng={selectedPosition.longitude} />
          )}
          <TodayTrail deviceId={selectedId} />
          {geofence && selectedId && (
            <Circle
              center={[geofence.center_lat, geofence.center_lon]}
              radius={geofence.radius_m}
              pathOptions={{ color: selectedColor, fillColor: selectedColor, fillOpacity: 0.08, weight: 1.5, dashArray: '6 4' }}
            />
          )}
          {devices.map((d) => {
            const pos = positions[d.id];
            if (!pos || pos.latitude === 0) return null;
            const color = colorForIndex(deviceIndex[d.id] ?? 0);
            const label = d.plate_number || d.name;
            return (
              <Marker
                key={d.id}
                position={[pos.latitude, pos.longitude]}
                icon={makeFleetIcon(color, label)}
                eventHandlers={{ click: () => onSelect(d.id) }}
              >
                <Popup>
                  <strong>{d.name}</strong>
                  {d.plate_number && <><br />{d.plate_number}</>}
                  <br />
                  {new Date(pos.timestamp + 'Z').toLocaleString()}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
      {selectedId && selectedPosition && (
        <div className="map-status-bar">
          <div className="map-status-line map-status-indicators">
            {selectedPosition.attributes?.ignition != null && (
              <span
                style={{ color: selectedPosition.attributes.ignition ? '#ffc107' : '#666666', display: 'inline-flex', alignItems: 'center' }}
                title={selectedPosition.attributes.ignition ? 'Zapłon włączony' : 'Zapłon wyłączony'}
              >
                <KeyRound size={16} />
              </span>
            )}
            {selectedPosition.speed != null && (() => {
              const kmh = Math.round(selectedPosition.speed * 1.852);
              const displaySpeed = Math.round(kmToUnit(kmh, unit));
              return (
                <span className="speed-display" style={{ color: kmh > 0 ? '#ffffff' : '#555' }}>
                  {String(displaySpeed).padStart(3, '0')} {speedUnitLabel(unit)}
                </span>
              );
            })()}
            {selectedPosition.attributes?.power != null && (
              <span style={{ color: powerColor(selectedPosition.attributes.power) }}>
                ⚡ {selectedPosition.attributes.power.toFixed(2)}V
              </span>
            )}
            {selectedPosition.attributes?.battery != null && selectedPosition.attributes.battery > 0 && (
              <span style={{ color: '#aaa' }}>
                🔋 {selectedPosition.attributes.battery.toFixed(2)}V
              </span>
            )}
            {selectedPosition.attributes?.sat != null && selectedPosition.attributes.sat > 0 && (
              <span style={{ color: satColor(selectedPosition.attributes.sat) }}>
                🛰️ {selectedPosition.attributes.sat}
              </span>
            )}
            {odometerKm != null && <OdometerDisplay km={odometerKm} unit={unit} />}
          </div>
          <div className="map-status-line">
            {selectedDevice?.name} — Ostatnia aktualizacja: {new Date(selectedPosition.timestamp + 'Z').toLocaleTimeString()}
          </div>
        </div>
      )}
    </div>
  );
}
